import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import SignaturePad from '../../components/SignaturePad';
import { api } from '../../api';
import toast from 'react-hot-toast';
import {
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  MapPin,
  RefreshCw,
  UserCheck,
  X
} from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/format';

interface StudentSheet {
  _id: string;
  course: { _id: string; title: string } | string | null;
  title: string;
  date: string;
  period: string;
  startTime: string;
  endTime: string;
  modality: string;
  location: string;
  trainerName: string;
  status: string;
  signable: boolean;
  myEntry: { status: string; signedAt: string | null; signed: boolean; method?: string; comment?: string } | null;
}

const PERIOD_LABELS: Record<string, string> = {
  matin: 'Matin',
  'apres-midi': 'Après-midi',
  journee: 'Journée'
};

const MODALITY_LABELS: Record<string, string> = {
  presentiel: 'Présentiel',
  distanciel: 'Distanciel',
  mixte: 'Mixte'
};

const STATUS_LABELS: Record<string, string> = {
  present: 'Présent',
  retard: 'En retard',
  absent: 'Absent',
  excuse: 'Excusé'
};

/** Rendu défensif : `course` peut arriver peuplé (objet) ou brut (chaîne). */
const courseLabel = (course: StudentSheet['course']): string => {
  if (!course) return '—';
  if (typeof course === 'string') return course;
  return course.title || '—';
};

/**
 * « Mon émargement » — l'apprenant signe chaque demi-journée de sa formation.
 * Route : /student/attendance — API : GET /api/student/attendance (+ POST /:id/sign)
 */
const StudentAttendance: React.FC = () => {
  const [sheets, setSheets] = useState<StudentSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSheet, setActiveSheet] = useState<StudentSheet | null>(null);
  const [signature, setSignature] = useState('');
  const [signatureStatus, setSignatureStatus] = useState<'' | 'present' | 'retard'>('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/student/attendance');
      setSheets(response.data?.sheets || []);
    } catch (error) {
      console.error('Erreur chargement émargements:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toSign = useMemo(() => sheets.filter((sheet) => sheet.signable), [sheets]);
  const signed = useMemo(() => sheets.filter((sheet) => sheet.myEntry?.signed), [sheets]);

  const closeModal = () => {
    setActiveSheet(null);
    setSignature('');
    setSignatureStatus('');
  };

  const handleSubmit = async () => {
    if (!activeSheet) return;
    if (!signatureStatus) {
      toast.error('Choisissez « Présent » ou « En retard »');
      return;
    }
    if (!signature) {
      toast.error('Merci de signer dans le cadre avant de valider');
      return;
    }
    try {
      setSubmitting(true);
      await api.post(`/student/attendance/${activeSheet._id}/sign`, {
        status: signatureStatus,
        signature
      });
      toast.success('Émargement enregistré');
      closeModal();
      await load();
    } catch (error) {
      // Le message d'erreur de l'API (403 clôturée, 409 doublon, 400 signature) est
      // déjà affiché par l'intercepteur axios : on ne double pas la notification.
      console.error('Erreur signature:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mon émargement</h1>
            <p className="mt-1 text-sm text-gray-500">
              Signez votre présence pour chaque demi-journée de formation — {signed.length}/{sheets.length} signée(s)
            </p>
          </div>
          <button
            onClick={load}
            className="flex items-center rounded-lg bg-brand-600 px-4 py-2 text-white transition-colors hover:bg-brand-700"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Rafraîchir
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="rounded-lg bg-brand-100 p-2">
                <ClipboardCheck className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Demi-journées</p>
                <p className="text-2xl font-bold text-gray-900">{sheets.length}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="rounded-lg bg-green-100 p-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Signées</p>
                <p className="text-2xl font-bold text-gray-900">{signed.length}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="rounded-lg bg-amber-100 p-2">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">À signer</p>
                <p className="text-2xl font-bold text-gray-900">{toSign.length}</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center rounded-lg border border-gray-200 bg-white py-12">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-600"></div>
          </div>
        ) : sheets.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white py-12 text-center">
            <UserCheck className="mx-auto mb-4 h-16 w-16 text-gray-300" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Aucune demi-journée à émarger</h3>
            <p className="text-gray-500">
              Les demi-journées de vos formations apparaîtront ici dès qu'un formateur les aura créées.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sheets.map((sheet) => {
              const entry = sheet.myEntry;
              const isSigned = !!entry?.signed;
              return (
                <div
                  key={sheet._id}
                  className="flex flex-col justify-between rounded-lg border border-gray-200 bg-white p-6 shadow-sm md:flex-row md:items-center"
                >
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">{sheet.title || 'Demi-journée'}</h3>
                      {isSigned ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                          Signée — {STATUS_LABELS[entry?.status || ''] || entry?.status || '—'}
                        </span>
                      ) : sheet.status === 'closed' ? (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                          Clôturée
                        </span>
                      ) : sheet.signable ? (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                          À signer
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                          Hors période de signature
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-brand-700">{courseLabel(sheet.course)}</p>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-600">
                      <span className="flex items-center">
                        <CalendarDays className="mr-1.5 h-4 w-4 text-gray-400" />
                        {formatDate(sheet.date)}
                      </span>
                      <span className="flex items-center">
                        <Clock className="mr-1.5 h-4 w-4 text-gray-400" />
                        {PERIOD_LABELS[sheet.period] || sheet.period}
                        {sheet.startTime || sheet.endTime ? ` — ${sheet.startTime || '?'} / ${sheet.endTime || '?'}` : ''}
                      </span>
                      {sheet.location ? (
                        <span className="flex items-center">
                          <MapPin className="mr-1.5 h-4 w-4 text-gray-400" />
                          {sheet.location}
                        </span>
                      ) : null}
                      {sheet.trainerName ? <span>Formateur : {sheet.trainerName}</span> : null}
                      <span>{MODALITY_LABELS[sheet.modality] || sheet.modality}</span>
                    </div>
                    {isSigned && entry?.signedAt ? (
                      <p className="text-xs text-gray-500">
                        Signé le {formatDateTime(entry.signedAt)}
                        {entry.method === 'manual' ? ' (saisie formateur)' : ''}
                        {entry.comment ? ` — ${entry.comment}` : ''}
                      </p>
                    ) : null}
                  </div>

                  <div className="mt-4 md:mt-0 md:ml-6">
                    {sheet.signable ? (
                      <button
                        onClick={() => {
                          setActiveSheet(sheet);
                          setSignature('');
                          setSignatureStatus('present');
                        }}
                        className="flex w-full items-center justify-center rounded-lg bg-brand-600 px-5 py-2.5 font-medium text-white transition-colors hover:bg-brand-700 md:w-auto"
                      >
                        <ClipboardCheck className="mr-2 h-4 w-4" />
                        Signer
                      </button>
                    ) : isSigned ? (
                      <span className="flex items-center text-sm font-medium text-green-700">
                        <CheckCircle2 className="mr-1.5 h-4 w-4" />
                        Émargement enregistré
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Aucune action requise</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {activeSheet ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-full w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Émargement de la demi-journée</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {activeSheet.title} — {courseLabel(activeSheet.course)} — {formatDate(activeSheet.date)} (
                  {PERIOD_LABELS[activeSheet.period] || activeSheet.period})
                </p>
              </div>
              <button onClick={closeModal} className="text-gray-400 transition-colors hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 px-6 py-5">
              <div>
                <span className="mb-2 block text-sm font-medium text-gray-700">Ma présence</span>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setSignatureStatus('present')}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                      signatureStatus === 'present'
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Présent
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignatureStatus('retard')}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                      signatureStatus === 'retard'
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    En retard
                  </button>
                </div>
              </div>

              <div>
                <span className="mb-2 block text-sm font-medium text-gray-700">Ma signature</span>
                <SignaturePad onChange={setSignature} disabled={submitting} height={200} />
              </div>

              <p className="text-xs text-gray-500">
                En validant, j'atteste de ma présence à cette demi-journée. La signature et l'horodatage sont
                conservés comme preuve d'assiduité (Qualiopi).
              </p>
            </div>

            <div className="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={closeModal}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !signature}
                className="rounded-lg bg-brand-600 px-4 py-2 font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                {submitting ? 'Enregistrement...' : "Valider mon émargement"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Layout>
  );
};

export default StudentAttendance;
